import { toast } from 'sonner';
import * as offlineQueue from './offlineQueue';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

let syncing = false;
const syncListeners = new Set<(isSyncing: boolean) => void>();

function notifySyncState(state: boolean) {
  syncing = state;
  syncListeners.forEach((fn) => fn(state));
}

/** Subscribe to sync-state changes. Returns unsubscribe function. */
export function onSyncStateChange(
  listener: (isSyncing: boolean) => void,
): () => void {
  syncListeners.add(listener);
  return () => {
    syncListeners.delete(listener);
  };
}

/** Whether synchronisation is currently running. */
export function isSyncing(): boolean {
  return syncing;
}

/** Replay all queued requests in FIFO order with exponential backoff. */
export async function replayQueue(): Promise<void> {
  if (syncing) return;

  const pending = await offlineQueue.getAll();
  if (pending.length === 0) return;

  notifySyncState(true);
  let successCount = 0;
  let failCount = 0;

  for (const entry of pending) {
    if (!navigator.onLine) {
      // Lost connection again mid-sync — stop
      break;
    }

    let succeeded = false;

    for (
      let attempt = entry.retryCount;
      attempt < MAX_RETRIES;
      attempt++
    ) {
      try {
        const response = await fetch(entry.url, {
          method: entry.method,
          headers: entry.headers,
          body: entry.body,
        });

        if (response.ok || (response.status >= 400 && response.status < 500)) {
          // Success or client error (don't retry 4xx — the request itself is bad)
          succeeded = true;
          break;
        }

        // Server error — retry with backoff
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
      } catch {
        // Network error — retry with backoff
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    if (succeeded) {
      await offlineQueue.remove(entry.id!);
      successCount++;
    } else {
      await offlineQueue.update(entry.id!, { retryCount: MAX_RETRIES });
      failCount++;
    }
  }

  notifySyncState(false);

  if (successCount > 0) {
    toast.success(
      `${successCount} action${successCount > 1 ? 's' : ''} synchronisée${successCount > 1 ? 's' : ''} avec succès`,
    );
  }
  if (failCount > 0) {
    toast.error(
      `${failCount} action${failCount > 1 ? 's' : ''} n'ont pas pu être synchronisées`,
    );
  }
}

/** Register Background Sync if available, and listen for online events. */
export function initSyncManager(): void {
  // Replay on reconnect
  window.addEventListener('online', () => {
    void replayQueue();
  });

  // Register Background Sync API if supported
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        if ('sync' in registration) {
          return (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register(
            'nzassa-offline-sync',
          );
        }
      })
      .catch(() => {
        // Background Sync not supported — online event fallback is enough
      });
  }

  // If already online and there are queued requests, replay immediately
  if (navigator.onLine) {
    void replayQueue();
  }
}
